import React, { useEffect } from "react";
import { View, StyleSheet, ScrollView } from "react-native";

import CustomText from "../../../common/general/CustomText/CustomText";
import AssignmentGradingSection from "./AssignmentGradingSection";
import { useGetAssignmentAndSubmissionBySubmissionIdQuery } from "../../../../graphql/generated/graphql";
import { useGradingCenterSubmissions } from "../../../../contexts/GradingCenterContext";
import FileSubmissionItem from "./StudentSubmission/FileSubmissionItem";

const getStatusColor = (status: string) => {
  switch (status) {
    case "RUNNING":
      return { bg: "#fef3c7", text: "#92400e" };
    case "NEEDS_REVIEW":
    case "Needs Review":
      return { bg: "#fecaca", text: "#dc2626" };
    case "COMPLETED":
    case "Completed":
      return { bg: "#d1fae5", text: "#065f46" };
    case "RETAKE_ALLOWED":
      return { bg: "#e0f2fe", text: "#0369a1" };
    default:
      return { bg: "#f3f4f6", text: "#6b7280" };
  }
};

const AssignmentGradingPanel: React.FC = () => {
  const { selectedSubmissionData, setUpdatedAssignmentPayload } = useGradingCenterSubmissions();

  const { data: assignmentSubmissionDetails } = useGetAssignmentAndSubmissionBySubmissionIdQuery({
    variables: { submissionId: selectedSubmissionData?.id || "" },
    skip: !selectedSubmissionData?.id || selectedSubmissionData?.submissionType === "Assessment",
  });

  const assignment = assignmentSubmissionDetails?.getAssignmentAndSubmissionBySubmissionId?.data[0]?.assignment;
  const submission = assignmentSubmissionDetails?.getAssignmentAndSubmissionBySubmissionId?.data[0]?.submission;

  useEffect(() => {
    const grade = submission?.grade ?? selectedSubmissionData?.grade ?? null;
    const feedback = submission?.feedback ?? "";

    setUpdatedAssignmentPayload({
      grade,
      feedback,
      retakeAllowed: assignment?.usersHasSchoolAssignmentConnection?.edges[0]?.properties?.retakeAllowed ?? false,
      retakeDeadline: assignment?.usersHasSchoolAssignmentConnection?.edges[0]?.properties?.retakeDeadline
        ? new Date(assignment.usersHasSchoolAssignmentConnection.edges[0].properties.retakeDeadline)
        : null,
    });
  }, [assignmentSubmissionDetails, submission, assignment, selectedSubmissionData]);

  const statusColors = getStatusColor(selectedSubmissionData?.status || "");

  return (
    <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
      {/* Assignment Info Header */}
      <View style={styles.assignmentInfoHeader}>
        <View style={styles.assignmentBasicInfo}>
          <CustomText
            text={assignment?.name}
            size="l"
            weight="bold"
            style={styles.assignmentTitle}
            useTranslationText={false}
          />
          <View style={styles.assignmentMeta}>
            {selectedSubmissionData?.courseName && (
              <View style={styles.metaTag}>
                <CustomText text={selectedSubmissionData.courseName} size="xs" useTranslationText={false} />
              </View>
            )}
            {selectedSubmissionData?.moduleName && (
              <View style={styles.metaTag}>
                <CustomText text={selectedSubmissionData.moduleName} size="xs" useTranslationText={false} />
              </View>
            )}
            {assignment?.due_date && (
              <View style={styles.metaTag}>
                <CustomText text={`Due: ${assignment.due_date}`} size="xs" useTranslationText={false} />
              </View>
            )}
          </View>
        </View>
        <View style={styles.assignmentStatusInfo}>
          <View style={styles.submissionStatus}>
            <View style={[styles.badge, { backgroundColor: statusColors.bg }]}>
              <CustomText
                text={selectedSubmissionData?.status}
                size="xs"
                style={[styles.statusText, { color: statusColors.text }]}
              />
            </View>
            {/* {selectedSubmissionData?.isResubmission && (
              <View style={styles.resubmissionTag}>
                <ArrowClockwise size={14} color="#D97706" />
                <CustomText text="Resubmission" size="xs" style={styles.resubmissionText} />
              </View>
            )} */}
          </View>
        </View>
      </View>

      {/* Student Info */}
      <View style={styles.studentSubmissionInfo}>
        <View style={styles.studentDetails}>
          <View style={styles.studentAvatar}>
            <CustomText
              text={selectedSubmissionData?.student?.initials}
              size="s"
              weight="bold"
              style={styles.avatarText}
              useTranslationText={false}
            />
          </View>
          <View style={styles.studentInfo}>
            <CustomText
              text={selectedSubmissionData?.student?.name}
              size="s"
              weight="bold"
              useTranslationText={false}
            />
            <CustomText
              text={selectedSubmissionData?.student?.email}
              size="xs"
              style={styles.email}
              useTranslationText={false}
            />
          </View>
        </View>
        <View style={styles.submissionDetails}>
          <CustomText text="submitted_on" size="xs" interpolation={{ value: selectedSubmissionData?.submittedOn }} />
          <CustomText
            text="submitted_time"
            size="xs"
            interpolation={{ value: selectedSubmissionData?.submittedTime }}
          />
        </View>
      </View>

      {/* Student Submission */}
      <View style={styles.studentSubmissionSection}>
        <CustomText text="Student Submission" weight="bold" style={styles.sectionTitle} />
        <View style={styles.filesContainer}>
          {submission?.files?.map((file) => <FileSubmissionItem key={file.id} file={file} />)}
        </View>
      </View>

      {/* Grading Section */}
      <AssignmentGradingSection />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    padding: 24,
  },
  assignmentInfoHeader: {
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 20,
  },
  assignmentBasicInfo: {
    flex: 1,
  },
  assignmentTitle: {
    color: "#111827",
    marginBottom: 8,
  },
  assignmentMeta: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  metaTag: {
    backgroundColor: "#F2F4F7",
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  assignmentStatusInfo: {
    alignItems: "flex-end",
  },
  submissionStatus: {
    alignItems: "flex-end",
    gap: 8,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
    alignSelf: "flex-start",
  },
  statusText: {
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  // resubmissionTag: {
  //   backgroundColor: "#FEF3C7",
  //   borderWidth: 1,
  //   borderColor: "#F59E0B",
  //   borderRadius: 20,
  //   paddingHorizontal: 10,
  //   paddingVertical: 6,
  //   flexDirection: "row",
  //   alignItems: "center",
  //   gap: 6,
  // },
  // resubmissionText: {
  //   color: "#D97706",
  // },
  studentSubmissionInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#EFF6FF",
    borderWidth: 1,
    borderColor: "#BFDBFE",
    borderRadius: 10,
    padding: 16,
    marginBottom: 24,
  },
  studentDetails: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  studentAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: "#3B82F6",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    color: "#FFFFFF",
  },
  studentInfo: {
    flex: 1,
  },
  email: {
    color: "#6B7280",
    marginTop: 2,
  },
  submissionDetails: {
    alignItems: "flex-end",
  },
  studentSubmissionSection: {
    marginBottom: 32,
  },
  sectionTitle: {
    color: "#374151",
    marginBottom: 8,
  },
  filesContainer: {
    gap: 12,
  },
});

export default AssignmentGradingPanel;
